import { useState, useRef, useEffect } from "react";
import usePageMeta from "../hooks/usePageMeta";
import { useAppPermissions } from "../context/PermissionContext";
import API from "../api.js";
import { BASE_URL } from "../config";
import { resolveBackendUrl } from "../utils/accountAvatarUrl";

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  const accountId = localStorage.getItem("accountId");
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (accountId) headers['x-author-account-id'] = `${accountId}`;
  return headers;
};

// Safe helper to stop active MediaStream tracks without requesting new hardware sessions
const stopStreamTracks = (stream) => {
  if (!stream) return;
  try {
    const tracks = stream.getTracks ? stream.getTracks() : [];
    tracks.forEach((track) => {
      try {
        track.stop();
        track.enabled = false;
      } catch (_) {}
    });
  } catch (_) {}
};

export default function FaceRecognitionTest() {
  usePageMeta("Face Recognition Test");
  const { canWriteAdmin } = useAppPermissions();
  const adminReadOnly = !canWriteAdmin;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [result, setResult] = useState(null);
  const isMountedRef = useRef(true);

  // Load accounts with profile pictures
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/face/test-accounts`, {
          headers: getAuthHeaders()
        });
        const data = await response.json();
        if (data.success) {
          setAccounts(data.accounts || []);
        } else {
          setError("Failed to load accounts");
        }
      } catch (err) {
        console.error("Error loading accounts:", err);
        setError("Failed to load accounts");
      }
    };
    loadAccounts();
  }, []);

  // Start camera when account is selected
  useEffect(() => {
    if (!selectedAccount) {
      // Stop camera if no account selected
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
          track.enabled = false;
        });
        streamRef.current = null;
      }
      return;
    }

    isMountedRef.current = true;
    
    // Request camera access
    const startCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
        });
        
        if (!isMountedRef.current) {
          mediaStream.getTracks().forEach(track => {
            track.stop();
            track.enabled = false;
          });
          return;
        }
        
        streamRef.current = mediaStream;
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (err) {
        console.error("Error accessing camera:", err);
        if (isMountedRef.current) {
          setError("Unable to access camera. Please check permissions.");
        }
      }
    };

    startCamera();

    // Cleanup
    return () => {
      isMountedRef.current = false;
      
      if (streamRef.current) {
        stopStreamTracks(streamRef.current);
        streamRef.current = null;
      }
      
      if (videoRef.current) {
        try {
          if (videoRef.current.srcObject) {
            stopStreamTracks(videoRef.current.srcObject);
          }
          videoRef.current.srcObject = null;
        } catch (e) {
          console.error("Error cleaning up video:", e);
        }
      }
    };
  }, [selectedAccount]);

  const stopCamera = () => {
    isMountedRef.current = false;
    
    if (streamRef.current) {
      stopStreamTracks(streamRef.current);
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      try {
        if (videoRef.current.srcObject) {
          stopStreamTracks(videoRef.current.srcObject);
        }
        videoRef.current.srcObject = null;
      } catch (e) {
        console.error("Error stopping camera:", e);
      }
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/jpeg', 0.8);
    }
    return null;
  };

  const handleTest = async () => {
    if (!selectedAccount) {
      setError("Please select an account first");
      return;
    }

    setIsVerifying(true);
    setError("");
    setResult(null);

    try {
      const capturedImage = capturePhoto();
      
      if (!capturedImage) {
        throw new Error("Failed to capture image from camera");
      }

      console.log("Testing face recognition for account:", selectedAccount.account_id);

      const response = await fetch(`${BASE_URL}/api/face/test-recognize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          imageBase64: capturedImage,
          accountId: selectedAccount.account_id
        })
      });

      const data = await response.json();
      console.log("Face recognition test response:", data);

      setResult(data);

    } catch (err) {
      console.error("Face recognition test error:", err);
      setError(err.response?.data?.error || err.message || "Face recognition test failed");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleReset = () => {
    setSelectedAccount(null);
    setResult(null);
    setError("");
    stopCamera();
  };

  return (
    <fieldset
      disabled={adminReadOnly}
      className="border-0 m-0 p-0 min-w-0 min-h-0"
    >
    <div className="container mx-auto p-6 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6">Face Recognition Test</h1>

      {/* Account Selection */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3">1. Select Account</h2>
        {loading ? (
          <div className="text-center py-4">Loading accounts...</div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No accounts with profile pictures found</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {accounts.map((account) => (
              <button
                key={account.account_id}
                onClick={() => {
                  setSelectedAccount(account);
                  setResult(null);
                  setError("");
                }}
                className={`p-3 border-2 rounded-lg text-left transition-colors ${
                  selectedAccount?.account_id === account.account_id
                    ? 'border-[#DC1F00] bg-red-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  {account.profile_picture_url ? (
                    <img
                      src={resolveBackendUrl(account.profile_picture_url)}
                      alt={`${account.first_name} ${account.last_name}`}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-400 text-xs">No Photo</span>
                    </div>
                  )}
                  <div>
                    <div className="font-semibold text-sm">
                      {account.first_name} {account.last_name}
                    </div>
                    <div className="text-xs text-gray-500">ID: {account.account_id}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected Account Info */}
      {selectedAccount && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">2. Selected Account</h2>
          <div className="flex items-center gap-4">
            {selectedAccount.profile_picture_url ? (
              <img
                src={resolveBackendUrl(selectedAccount.profile_picture_url)}
                alt={`${selectedAccount.first_name} ${selectedAccount.last_name}`}
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-300"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-gray-400">No Photo</span>
              </div>
            )}
            <div>
              <div className="text-xl font-semibold">
                {selectedAccount.first_name} {selectedAccount.last_name}
              </div>
              <div className="text-sm text-gray-600">Account ID: {selectedAccount.account_id}</div>
              {selectedAccount.email && (
                <div className="text-sm text-gray-600">Email: {selectedAccount.email}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Camera Preview */}
      {selectedAccount && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">3. Scan Face</h2>
          <div className="relative rounded-lg overflow-hidden bg-black mb-4">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-64 object-cover"
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-75">
                <p className="text-white text-center px-4 text-sm">{error}</p>
              </div>
            )}
            {/* Face outline guide */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-56 border-4 border-white rounded-full opacity-50"></div>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleTest}
              disabled={isVerifying || !!error}
              className="flex-1 bg-[#DC1F00] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#b91900] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isVerifying ? "TESTING..." : "TEST FACE RECOGNITION"}
            </button>
            <button
              onClick={handleReset}
              className="px-4 bg-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
            >
              RESET
            </button>
          </div>
        </div>
      )}

      {/* Test Results */}
      {result && (
        <div className={`bg-white rounded-lg shadow p-4 mb-6 ${
          result.matched ? 'border-2 border-green-500' : 'border-2 border-red-500'
        }`}>
          <h2 className="text-lg font-semibold mb-3">4. Test Results</h2>
          {result.matched ? (
            <div className="space-y-2">
              <div className="text-green-600 font-semibold text-lg">✅ Face Matched!</div>
              <div className="text-sm text-gray-600">
                <div>Similarity: <span className="font-semibold">{result.similarity?.toFixed(2)}%</span></div>
                <div>Account: {result.firstName} {result.lastName} (ID: {result.accountId})</div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-red-600 font-semibold text-lg">❌ Face Not Matched</div>
              <div className="text-sm text-gray-600">
                <div>Reason: {result.reason || "FACE_NOT_MATCHED"}</div>
                {result.message && <div>Message: {result.message}</div>}
                {result.similarity !== undefined && (
                  <div>Best Similarity: {result.similarity.toFixed(2)}%</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
        <h3 className="font-semibold mb-2">Instructions:</h3>
        <ol className="list-decimal list-inside space-y-1">
          <li>Select an account from the list above (only accounts with profile pictures are shown)</li>
          <li>Review the selected account's profile picture</li>
          <li>Position your face within the camera frame (or use the same person's face as the profile picture)</li>
          <li>Click "TEST FACE RECOGNITION" to test</li>
          <li>Review the results to see if the face matches</li>
        </ol>
      </div>
    </div>
    </fieldset>
  );
}

